import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { CampoBusca } from '../componentes/CampoBusca.js'
import { useUsuarioAtual } from '../dados/usuarioAtual.js'
import { AcoesUsuario } from './AcoesUsuario.js'
import { PerfisUsuario } from './PerfisUsuario.js'
import { LimpezaCadastros } from './LimpezaCadastros.js'
import { NovoUsuario } from './NovoUsuario.js'
import { ObrasUsuario } from './ObrasUsuario.js'
import { OrigemRm } from './OrigemRm.js'
import { SincronizacaoRm } from './SincronizacaoRm.js'
import { api, type PerfilNome, type UsuarioLinha } from '../dados/api.js'
import { PainelAdmin, QuandoFoi, SemRedeAqui, rotuloPerfil } from './admin-comum.js'

/// Quem tem acesso ao sistema. Só admin.
///
/// Esta tela **não responde quem pode o quê por obra** — isso é a soma de perfil e
/// vínculos, e está no detalhe de cada pessoa. O que ela responde de relance é a
/// pergunta da virada: quantos já entraram no sistema novo e quantos ainda estão só
/// no Power Apps.

const PERFIS: PerfilNome[] = [
  'ADMIN',
  'GERENTE_CONTRATO',
  'ENGENHEIRO_SEGURANCA',
  'TECNICO_SEGURANCA',
  'ENCARREGADO',
  'COLABORADOR',
  'RESPONSAVEL_NC',
]

export function Usuarios() {
  const [busca, setBusca] = useState('')
  const [perfil, setPerfil] = useState<PerfilNome | ''>('')
  const [situacao, setSituacao] = useState<'ativos' | 'inativos' | 'todos'>('ativos')
  const [acesso, setAcesso] = useState<'comAcesso' | 'soCadastro' | 'todos'>('comAcesso')
  const [semAcesso, setSemAcesso] = useState(false)
  const [pagina, setPagina] = useState(1)
  const [aberto, setAberto] = useState<string | null>(null)

  // A busca vai direto para o servidor: são 884 pessoas, não cabe filtrar no
  // navegador sem baixar tudo.
  const filtros = { busca, perfil, situacao, acesso, semAcesso, pagina }
  const lista = useQuery({
    queryKey: ['admin', 'usuarios', filtros],
    queryFn: () => api.adminUsuarios(filtros),
    placeholderData: (anterior) => anterior,
  })
  const resumo = useQuery({
    queryKey: ['admin', 'usuarios', 'resumo'],
    queryFn: () => api.adminUsuariosResumo(),
  })

  function mudarFiltro(acao: () => void) {
    acao()
    setPagina(1)
  }

  /// Leva a lista até uma pessoa, com os filtros abertos o bastante para ela
  /// aparecer — um cadastro sem acesso some do filtro padrão.
  function mostrar(email: string, id?: string) {
    setBusca(email)
    setPerfil('')
    setAcesso('todos')
    setSituacao('todos')
    setSemAcesso(false)
    setPagina(1)
    setAberto(id ?? null)
  }

  return (
    <PainelAdmin titulo="Usuários" subtitulo="Quem tem acesso ao Sistema QSMS">
      {resumo.data && (
        <>
          <div className="mb-2 grid grid-cols-3 gap-2">
            <Numero rotulo="Com acesso" valor={resumo.data.comAcesso} />
            <Numero
              rotulo="Já entraram"
              valor={resumo.data.jaEntraram}
              detalhe={`de ${resumo.data.comAcesso}`}
            />
            <Numero rotulo="Só cadastro" valor={resumo.data.soCadastro} />
          </div>
          <p className="mb-2 text-xs text-slate-500">
            <strong>Só cadastro</strong> são pessoas que vieram do vínculo funcionário–obra do RM.
            Existem para aparecer nas listas de encarregado e fiscal, e não conseguem entrar no
            sistema.
          </p>
          <p className="mb-4 rounded bg-marinho-50 px-3 py-2 text-xs text-marinho">
            {resumo.data.grupoAcesso ? (
              <>
                <strong>Quem entra:</strong> quem está no grupo{' '}
                <strong>{resumo.data.grupoAcesso}</strong> do Entra ID. No primeiro login, quem
                ainda não tem cadastro ganha um aqui, como colaborador e sem obra. Tirar o acesso
                ou desativar nesta tela vale mesmo para quem está no grupo.
              </>
            ) : (
              <>
                <strong>Quem entra:</strong> quem tem acesso liberado nesta tela. O grupo do Entra
                ID ainda não é conferido no login.
              </>
            )}
          </p>
        </>
      )}

      <SincronizacaoRm />
      <NovoUsuario grupoAcesso={resumo.data?.grupoAcesso ?? null} aoMostrar={mostrar} />
      <LimpezaCadastros />

      {/* No computador, busca e filtros numa linha só. */}
      <div className="cartao mb-4 space-y-3 p-3 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-center lg:gap-2 lg:space-y-0">
        <CampoBusca
          valor={busca}
          aoMudar={(v) => mudarFiltro(() => setBusca(v))}
          placeholder="Buscar por nome ou e-mail"
        />
        <div className="flex flex-wrap gap-2 lg:grid lg:grid-cols-[repeat(3,minmax(0,1fr))_auto] lg:items-center">
          <select
            value={perfil}
            onChange={(e) => mudarFiltro(() => setPerfil(e.target.value as PerfilNome | ''))}
            className="campo"
          >
            <option value="">Todos os perfis</option>
            {PERFIS.map((p) => (
              <option key={p} value={p}>
                {rotuloPerfil[p]}
                {resumo.data?.porPerfil[p] ? ` (${resumo.data.porPerfil[p]})` : ''}
              </option>
            ))}
          </select>
          <select
            value={acesso}
            onChange={(e) => mudarFiltro(() => setAcesso(e.target.value as typeof acesso))}
            className="campo"
          >
            <option value="comAcesso">Com acesso ao sistema</option>
            <option value="soCadastro">Só cadastro (não entram)</option>
            <option value="todos">Todos</option>
          </select>
          <select
            value={situacao}
            onChange={(e) => mudarFiltro(() => setSituacao(e.target.value as typeof situacao))}
            className="campo"
          >
            <option value="ativos">Ativos</option>
            <option value="inativos">Inativos</option>
            <option value="todos">Todos</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={semAcesso}
              onChange={(e) => mudarFiltro(() => setSemAcesso(e.target.checked))}
              className="h-4 w-4 rounded border-slate-300"
            />
            Nunca entraram
          </label>
        </div>
      </div>

      {lista.isError && <SemRedeAqui erro={lista.error} />}
      {lista.isLoading && <p className="py-8 text-center text-slate-500">Carregando…</p>}

      {lista.data && (
        <>
          <p className="mb-2 text-sm text-slate-600">
            {lista.data.total === 0
              ? 'Nenhuma pessoa com esses filtros.'
              : `${lista.data.total} ${lista.data.total === 1 ? 'pessoa' : 'pessoas'}`}
          </p>

          <ul className="grid gap-2 xl:grid-cols-2 xl:items-start">
            {lista.data.usuarios.map((u) => (
              <li key={u.id}>
                <Linha
                  usuario={u}
                  aberto={aberto === u.id}
                  onAlternar={() => setAberto(aberto === u.id ? null : u.id)}
                />
              </li>
            ))}
          </ul>

          {lista.data.paginas > 1 && (
            <Paginacao
              pagina={lista.data.pagina}
              paginas={lista.data.paginas}
              onIr={setPagina}
              carregando={lista.isFetching}
            />
          )}
        </>
      )}
    </PainelAdmin>
  )
}

function Linha({
  usuario,
  aberto,
  onAlternar,
}: {
  usuario: UsuarioLinha
  aberto: boolean
  onAlternar: () => void
}) {
  return (
    <div className="cartao overflow-hidden">
      <button onClick={onAlternar} className="flex w-full items-center gap-3 p-3 text-left">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-slate-800">
            {usuario.nome}
            {!usuario.ativo && <span className="ml-2 text-xs font-normal text-slate-400">inativo</span>}
          </p>
          <p className="truncate text-xs text-slate-500">{usuario.email}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {usuario.perfis.map((p) => (
              <span key={p} className="etiqueta bg-marinho-50 text-marinho">
                {rotuloPerfil[p]}
              </span>
            ))}
            {!usuario.acessoLiberado && (
              <span className="etiqueta bg-slate-100 text-slate-500">só cadastro</span>
            )}
            {usuario.acessoLiberado && usuario.perfis.length === 0 && (
              <span className="etiqueta bg-slate-100 text-slate-500">sem perfil</span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right text-xs text-slate-500">
          <p>{usuario.obras} {usuario.obras === 1 ? 'obra' : 'obras'}</p>
          <p className={usuario.ultimoLogin ? '' : 'text-slate-400'}>
            {usuario.ultimoLogin ? (
              <QuandoFoi iso={usuario.ultimoLogin} />
            ) : usuario.acessoLiberado ? (
              'nunca entrou'
            ) : (
              'sem acesso'
            )}
          </p>
        </div>
      </button>
      {aberto && <Detalhe id={usuario.id} />}
    </div>
  )
}

function Detalhe({ id }: { id: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'usuario', id],
    queryFn: () => api.adminUsuario(id),
  })
  // O servidor também recusa, mas esconder o botão evita o clique que só dá erro.
  const { usuario: eu } = useUsuarioAtual()
  const euId = eu?.id ?? null

  if (isLoading) return <p className="border-t border-slate-100 p-3 text-sm text-slate-500">Carregando…</p>
  if (isError) {
    return (
      <div className="border-t border-slate-100 p-3">
        <SemRedeAqui erro={error} />
      </div>
    )
  }
  if (!data) return null

  const ativos = data.dispositivos.filter(
    (d) => !d.revogadoEm && new Date(d.expiraEm) > new Date(),
  )

  return (
    <div className="space-y-3 border-t border-slate-100 bg-slate-50 p-3 text-sm">
      <div>
        <ObrasUsuario usuario={data} />
        <OrigemRm usuarioId={data.id} tipo="obras" manual={data.obrasManuais} />
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Aparelhos com sessão ({ativos.length} {ativos.length === 1 ? 'ativa' : 'ativas'})
        </p>
        {data.dispositivos.length === 0 ? (
          <p className="text-slate-500">Nenhum. A pessoa nunca fez login no sistema novo.</p>
        ) : (
          <ul className="space-y-1">
            {data.dispositivos.slice(0, 6).map((d) => {
              const vencido = new Date(d.expiraEm) <= new Date()
              return (
                <li key={d.id} className="flex flex-wrap items-baseline gap-x-2 text-slate-700">
                  <span className="font-medium">{d.descricao ?? 'Aparelho'}</span>
                  <span className="text-xs text-slate-500">
                    entrou <QuandoFoi iso={d.criadoEm} />
                    {d.ultimoUso && <> · usou <QuandoFoi iso={d.ultimoUso} /></>}
                  </span>
                  {d.revogadoEm ? (
                    <span className="etiqueta bg-slate-200 text-slate-600">revogado</span>
                  ) : vencido ? (
                    <span className="etiqueta bg-slate-200 text-slate-600">expirado</span>
                  ) : (
                    <span className="etiqueta bg-leve/10 text-leve">ativo</span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Cadastrado em {new Date(data.criadoEm).toLocaleDateString('pt-BR')}
        {data.entraOid ? ' · conta Microsoft vinculada' : ' · conta Microsoft ainda não vinculada'}
      </p>

      <div>
        <PerfisUsuario usuarioId={data.id} perfis={data.perfis} souEu={data.id === euId} />
        <OrigemRm usuarioId={data.id} tipo="perfis" manual={data.perfisManuais} />
      </div>

      <div className="-mx-3 -mb-3">
        <AcoesUsuario usuario={data} souEu={data.id === euId} />
      </div>
    </div>
  )
}

function Numero({ rotulo, valor, detalhe }: { rotulo: string; valor: number; detalhe?: string }) {
  return (
    <div className="cartao p-3 text-center">
      <p className="text-2xl font-bold text-marinho">{valor.toLocaleString('pt-BR')}</p>
      <p className="text-xs text-slate-500">{rotulo}</p>
      {detalhe && <p className="text-[11px] text-slate-400">{detalhe}</p>}
    </div>
  )
}

export function Paginacao({
  pagina,
  paginas,
  onIr,
  carregando,
}: {
  pagina: number
  paginas: number
  onIr: (p: number) => void
  carregando?: boolean
}) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3">
      <button
        onClick={() => onIr(pagina - 1)}
        disabled={pagina <= 1 || carregando}
        className="botao-neutro"
      >
        ← Anterior
      </button>
      <span className="text-sm text-slate-600">
        {pagina} de {paginas}
      </span>
      <button
        onClick={() => onIr(pagina + 1)}
        disabled={pagina >= paginas || carregando}
        className="botao-neutro"
      >
        Próxima →
      </button>
    </div>
  )
}
